/* eslint-disable @typescript-eslint/unbound-method */
import { createAgentSession, getAgentSession, getAgentSessionControls, updateAgentSessionStep } from "@/modules/sessions/requests";
import {
  AgentSessionControl,
  AgentSessionResponse,
  AgentSessionStatus,
  AgentSessionStep,
  AgentSessionStepType,
  AgentSessionType,
  CommonRagCreateRequest,
  CustomGapAgentSession,
  SOC2GapAgentSession
} from "@/modules/sessions/types";
import { AGENT_ROUTES, ROUTES } from "@/shared/constants/routes";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { addNotification } from "@/shared/states/notification";
import { userStateSelector } from "@/shared/states/user";
import { AiResponseType } from "@/shared/types/user";
import { NavigateFunction } from "react-router-dom";
import { emitRagCreate, getFrameworksList } from "../requests";
import { getRAGExcelFromJSON } from "../requests/";
import { getAgentData, getAgentStateActions } from "../states";
import {
  AGENT_TYPES,
  AgentData,
  AgentSourceFile,
  AgentSpecificReviewResponseType,
  ComplianceStatus,
  ConfidenceTypes,
  GapAssessmentTypes,
  ReviewResponseData,
  ReviewSourceTypes,
  SOC2ComplianceStatusDeprecated,
  Soc2GapReviewResponseTableRow,
} from "../types";
import { ResponseSchemaVersion, SOC2GapReviewResponse, TestResultsValue } from "../types/risk-and-gap.ts";
import { useAgentSocket } from "../utils/agent-socket.ts";
import { AutoSaveFunctionArgs } from "../utils/autosave";
import { getFileNameForDownloadReport } from "../utils/downloadReport.ts";
import { getRenderType } from "../utils/get-render-type";
import { getResponseTypes } from "../utils/get-response-type";
import { downloadFileWithCheck } from "../utils/index.ts";
import { convertSnakeToCapitalized } from "../utils/snake-to-capital.ts";
import { handleSocketResponse } from "../utils/socket-response";
import { processFileForCustomizeControls } from "./customize-control.use-case";

// Type guard to check if a step is a customize controls step
function isCustomizeControlsStep(step: AgentSessionStep): step is AgentSessionStep & { type: AgentSessionStepType.CONTROL_SELECTION | AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING; data: { url: string | null; metadata?: any } } {
  return (step.type === AgentSessionStepType.CONTROL_SELECTION || step.type === AgentSessionStepType.NIST_CSF_EVIDENCE_MAPPING) && 
         step.data && 
         typeof step.data === 'object' && 
         'url' in step.data;
}

interface ProcessFileForSoc2GapAssessmentArgs {
  name: string;
  sourceFiles: AgentSourceFile[];
  navigate: NavigateFunction;
  customize: boolean;
  sessionType?: AgentSessionType.SOC2_GAP_TYPE1 | AgentSessionType.SOC2_GAP | AgentSessionType.SOC2_GAP_TYPE2 | AgentSessionType.GAP_CUSTOM_FRAMEWORK;
  reassessOption?: string;
  agentDataCurrent?: AgentData<AGENT_TYPES, GapAssessmentTypes> | undefined;
  evidence_mapping?: boolean;
}

interface CustomAssessmentArgs extends ProcessFileForSoc2GapAssessmentArgs{
  frameworkId?: string;
}

export const processFileForSoc2GapAssessment = async ({
  name,
  sourceFiles,
  customize,
  navigate,
  sessionType,
  reassessOption,
  agentDataCurrent,
  evidence_mapping
}: ProcessFileForSoc2GapAssessmentArgs) => {
  const agentSessionType = sessionType;
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;
  const source_urls = sourceFiles.map((file) => file.url);

  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  const gapAssessmentRequest: CommonRagCreateRequest = {
    doc_type: "json",
    response_quality,
    source_urls,
  };

  // const getPrevUrl = () => {
  //   let url = '';
  //   for (const step of agentDataCurrent?.stepData || []) {
  //     let castedStep = step as EditResponseAgentSessionStep;
  //     if (castedStep.data && castedStep.data.url) {
  //       url = castedStep.data.url;
  //     }
  //   }
  //   return url;
  // }

  if (reassessOption) {
    const retainedOption = reassessOption === 'RETAIN_EDITED_ACCEPTED' ? ['edited', 'approved'] : reassessOption === 'RETAIN_ACCEPTED' ? ['approved'] : [];
    gapAssessmentRequest.assessment_type = 'reassessment';
    gapAssessmentRequest.reassessment = {
      // previous_response_url: getPrevUrl(),
      retained: retainedOption
    }
  } else {
    gapAssessmentRequest.assessment_type = 'assessment';
  }

  gapAssessmentRequest.evidence_mapping = evidence_mapping;

  let session;
  let steps;
  if (reassessOption) {
    session = agentDataCurrent?.sessionData;
    steps = agentDataCurrent?.stepData;
  } else {

    const {
      data
    } = await createAgentSession({
      name: name ?? "SOC 2 Gap Assessment",
      type: agentSessionType ?? AgentSessionType.SOC2_GAP, // Provide a default value
      customize,
      [agentSessionType ?? AgentSessionType.SOC2_GAP]: gapAssessmentRequest,
    });
    session = data.session;
    steps = data.steps;
  }

  if (!session || !session.id) {
    throw new Error("An error occurred");
  }

  const agentType = AGENT_TYPES.GAP_ASSESSMENT;
  const agentSubType =
    session.type === AgentSessionType.SOC2_GAP_TYPE1
      ? GapAssessmentTypes.SOC2_TYPE1
      : session.type === AgentSessionType.SOC2_GAP_TYPE2
        ? GapAssessmentTypes.SOC2_TYPE2
        : session.type === AgentSessionType.GAP_CUSTOM_FRAMEWORK ? GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK : GapAssessmentTypes.SOC2;
  const statusInProgressSessionStep: string[] = [];
  if (!customize) {
    statusInProgressSessionStep.push(
      sessionType === AgentSessionType.SOC2_GAP_TYPE1
        ? AgentSessionStepType.SOC2_TYPE1
        : AgentSessionStepType.SOC2_TYPE2,
    );
  }
  const task = sessionType;
  const statusCompleteSessionSteps = [AgentSessionStepType.LOAD_TEMPLATE, AgentSessionStepType.EXTRACT_CONTROLS];

  const stepData = steps?.reduce((acc, step) => {
    if (statusCompleteSessionSteps.includes(step.type)) {
      step.status = AgentSessionStatus.COMPLETE;
    }

    if (statusInProgressSessionStep.includes(step.type)) {
      step.status = reassessOption ? AgentSessionStatus.UNDER_REASSESSMENT : AgentSessionStatus.IN_PROGRESS;
    }

    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

  const customizeControlStep = stepData?.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION || step.type === AgentSessionStepType.SOC2_EVIDENCE_MAPPING);
  const agentData: AgentData<typeof agentType, typeof agentSubType> = {
    agentType: agentType,

    sessionData: session as SOC2GapAgentSession,
    stepData: stepData || [],
    subType: agentSubType,
    responseQuality: selectedResponse,
    mainData: {
      sourceFilesUrls: new Map(sourceFiles.map((file) => [file.fileName, file])),
      approvedIds: [],
      editedIds: [],
    },
  };

  const agent_session_step_id =
    steps?.find((step) => statusInProgressSessionStep.includes(step.type))?.id ??
    steps?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE)?.id ??
    "";

  const { setAgentData } = getAgentStateActions();
  setAgentData(session.id, agentData);

  if (customizeControlStep && customizeControlStep.status === AgentSessionStatus.INPUT_NEEDED) {
    if (isCustomizeControlsStep(customizeControlStep)) {
      await processFileForCustomizeControls(
        customizeControlStep.data.url,
        session,
        customizeControlStep
      );
    }
  } else {
    await emitRagCreate(
      {
        agent_session_id: session.id,
        agent_session_step_id,
        rag_input: { ...gapAssessmentRequest, controls_url: (customizeControlStep && isCustomizeControlsStep(customizeControlStep)) ? customizeControlStep.data.url ?? "" : "" },
        response_mode,
        task: (task ?? AgentSessionType.SOC2_GAP),
      },
      (response: any) => {
        handleSocketResponse(response, navigate);
      },
    );
  }
  navigate({
    pathname: `/agent/${session.id}/`,
  });
};


export const processFileForCustomGapAssessment = async ({
  name,
  sourceFiles,
  customize,
  navigate,
  sessionType,
  reassessOption,
  agentDataCurrent,
  evidence_mapping,
  frameworkId
}: CustomAssessmentArgs) => {
  const agentSessionType = AgentSessionType.GAP_CUSTOM_FRAMEWORK;
  const selectedResponse =
    userStateSelector.getState().aiResponseType === AiResponseType.NONE
      ? AiResponseType.LITE
      : userStateSelector.getState().aiResponseType;
  const source_urls = sourceFiles.map((file) => file.url);

  const { response_mode, response_quality } = getResponseTypes(selectedResponse);

  const gapAssessmentRequest: CommonRagCreateRequest = {
    doc_type: "json",
    response_quality,
    source_urls,
  };

  if (reassessOption) {
    const retainedOption = reassessOption === 'RETAIN_EDITED_ACCEPTED' ? ['edited', 'approved'] : reassessOption === 'RETAIN_ACCEPTED' ? ['approved'] : [];
    gapAssessmentRequest.assessment_type = 'reassessment';
    gapAssessmentRequest.reassessment = {
      // previous_response_url: getPrevUrl(),
      retained: retainedOption
    }
  } else {
    gapAssessmentRequest.assessment_type = 'assessment';
  }

  gapAssessmentRequest.evidence_mapping = evidence_mapping;

  gapAssessmentRequest.framework_id = frameworkId;

  let session;
  let steps;
  if (reassessOption) {
    session = agentDataCurrent?.sessionData;
    steps = agentDataCurrent?.stepData;
  } else {

    const {
      data
    } = await createAgentSession({
      name: name ?? "SOC 2 Custom Gap Assessment",
      type: agentSessionType as any,
      customize,
      [agentSessionType as any]: gapAssessmentRequest,
      
    });
    session = data.session;
    steps = data.steps;
  }

  if (!session || !session.id) {
    throw new Error("An error occurred");
  }

  const agentType = AGENT_TYPES.GAP_ASSESSMENT;
  const agentSubType = GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK;
  const statusInProgressSessionStep: string[] = [];
  if (!customize) {
    statusInProgressSessionStep.push(AgentSessionStepType.GAP_CUSTOM_FRAMEWORK);
  }
  const task = sessionType;
  const statusCompleteSessionSteps = [AgentSessionStepType.LOAD_TEMPLATE, AgentSessionStepType.EXTRACT_CONTROLS];

  const stepData = steps?.reduce((acc, step) => {
    if (statusCompleteSessionSteps.includes(step.type)) {
      step.status = AgentSessionStatus.COMPLETE;
    }

    if (statusInProgressSessionStep.includes(step.type)) {
      step.status = reassessOption ? AgentSessionStatus.UNDER_REASSESSMENT : AgentSessionStatus.IN_PROGRESS;
    }

    acc.push(step);
    return acc;
  }, [] as AgentSessionStep[]);

  const customizeControlStep = stepData?.find((step) => step.type === AgentSessionStepType.CONTROL_SELECTION || step.type === AgentSessionStepType.SOC2_EVIDENCE_MAPPING);
  const agentData: any = {
    agentType: agentType,

    sessionData: session as CustomGapAgentSession,
    stepData: stepData || [],
    subType: agentSubType,
    responseQuality: selectedResponse,
    mainData: {
      sourceFilesUrls: new Map(sourceFiles.map((file) => [file.fileName, file])),
      approvedIds: [],
      editedIds: [],
    },
  };

  const agent_session_step_id =
    steps?.find((step) => statusInProgressSessionStep.includes(step.type))?.id ??
    steps?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE)?.id ??
    "";

  const { setAgentData } = getAgentStateActions();
  setAgentData(session.id, agentData);

  if (customizeControlStep && customizeControlStep.status === AgentSessionStatus.INPUT_NEEDED) {
    if (isCustomizeControlsStep(customizeControlStep)) {
      await processFileForCustomizeControls(
        customizeControlStep.data.url,
        session,
        customizeControlStep
      );
    }
  } else {
    await emitRagCreate(
      {
        agent_session_id: session.id,
        agent_session_step_id,
        rag_input: { ...gapAssessmentRequest, controls_url: (customizeControlStep && isCustomizeControlsStep(customizeControlStep)) ? customizeControlStep.data.url ?? "" : "" },
        response_mode,
        task: task as AgentSessionType.GAP_CUSTOM_FRAMEWORK,
      },
      (response: any) => {
        handleSocketResponse(response, navigate);
      },
    );
  }
  navigate({
    pathname: `/agent/${session.id}/`,
  });
};

interface FinalGapReviewResponse extends SOC2GapReviewResponse {
  tsc_id: string;
  approved: boolean;
  edited: boolean;
  id: string;
}

export const generateFinalSoc2GapJSON = (agentId: string) => {
  const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }
  const { mainData } = agentData;
  const { reviewResponseData } = mainData;
  if (!reviewResponseData) {
    throw new Error("An error occurred");
  }
  const approvedIds = mainData.approvedIds;
  const editedIds = mainData.editedIds;
  const finalGapJson: FinalGapReviewResponse[] = [];

  reviewResponseData.forEach((item, id) => {
    const gap: FinalGapReviewResponse = {
      approved: approvedIds.includes(id),
      edited: editedIds.includes(id),
      tsc_id: item.find((item) => item.key === "tsc_id")?.value as string,
      id,
      gaps: item.find((item) => item.key === "gaps")?.value as string,
      recommendations: item.find((item) => item.key === "recommendations")?.value as string,
      confidence: item.find((item) => item.key === "confidence")?.value as ConfidenceTypes,
      justification: item.find((item) => item.key === "justification")?.value as string,
      sources: item.find((item) => item.key === "sources")?.value as ReviewSourceTypes[],
      observations: item.find((item) => item.key === "observations")?.value as string,
      compliance_status: item.find((item) => item.key === "compliance_status")?.value as SOC2ComplianceStatusDeprecated,
      llm_instructions: item.find((item) => item.key === "llm_instructions")?.value as string,
      trust_id: item.find((item) => item.key === "trust_id")?.value as string,
      tsc: item.find((item) => item.key === "tsc")?.value as string,
      control_number: item.find((item) => item.key === "control_number")?.value as number,
      control: item.find((item) => item.key === "control")?.value as string,
      category: item.find((item) => item.key === "category")?.value as string,
      assessment_criteria: item.find((item) => item.key === "assessment_criteria")?.value as string[],
      retriever_questions: item.find((item) => item.key === "retriever_questions")?.value as string[],
      zania_control_id: item.find((item) => item.key === "zania_control_id")?.value as string,
      control_id: item.find((item) => item.key === "control_id")?.value as string,
      improvement_opportunities: item.find((item) => item.key === "improvement_opportunities")?.value as string,
      improvement_recommendations: item.find((item) => item.key === "improvement_recommendations")?.value as string,
      toe: item.find((item) => item.key === "toe")?.value as string,
      tod: item.find((item) => item.key === "tod")?.value as string,
      criteria: item.find((item) => item.key === "criteria")?.value as string,
      version: item.find((item) => item.key === "version")?.value as ResponseSchemaVersion,
      test_results: item.find((item) => item.key === "test_results")?.value as TestResultsValue[],
    };
    finalGapJson.push(gap);
  });

  return finalGapJson;
};

export const handleAutoSaveSoc2 = async ({ agentId, markAsComplete }: AutoSaveFunctionArgs) => {
  const gapData = generateFinalSoc2GapJSON(agentId)

  const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }

  const { stepData } = agentData;
  const { staleUrl, selectedId } = agentData.mainData;

  const { setStaleUrl, updateAgentStepData } = getAgentStateActions();

  const editStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

  if (!editStepData) {
    throw new Error("An occurred while saving");
  }

  const stepUrl = editStepData?.data?.url;

  let currentStaleUrl = staleUrl || stepUrl;
  let markAsCompleteDone = false;

  if (!currentStaleUrl) {
    const signedUrl = await getSignedUrl({
      file_names: ["updated_gaps.json"],
      max_age: 86400,
    });
    const updatedStep = {
      ...editStepData,
      data: {
        url: signedUrl[0],
      },
      status: markAsComplete ? AgentSessionStatus.COMPLETE : AgentSessionStatus.INPUT_NEEDED,
    };
    const updatedSteps = stepData.map((step) => {
      if (step.id === updatedStep.id) {
        return updatedStep;
      }
      return step;
    });
    updateAgentStepData(agentId, updatedSteps);
    await updateAgentSessionStep(updatedStep);
    currentStaleUrl = signedUrl[0];
    markAsCompleteDone = true;
    setStaleUrl(agentId, currentStaleUrl);
  }
  const expiryDate = new URL(currentStaleUrl).searchParams.get("se");
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    const currentTime = new Date();
    const diff = expiry.getTime() - currentTime.getTime();
    if (diff < 0) {
      const signedUrl = await getSignedUrl({
        stale_urls: [currentStaleUrl],
        max_age: 86400,
      });
      currentStaleUrl = signedUrl[0];
      setStaleUrl(agentId, currentStaleUrl);
    }
  }

  const control = gapData.find((item) => item.id === selectedId);
  if (!control) return;

  const { emitControlEdit } = useAgentSocket();
  try {
    const agentData = getAgentData<
      AGENT_TYPES.GAP_ASSESSMENT,
      GapAssessmentTypes.SOC2
    >(agentId);
    const editedField = agentData?.mainData.lastEditedField;
    await emitControlEdit(
      currentStaleUrl,
      control,
      control.id,
      agentId,
      editStepData.id,
      editedField ?? "",
    );
  } catch (error) {
    console.error("Error in emitControlEdit:", error);
    throw error;
  }
};

export const generateFinalCustomGapAssessmentJSON = (agentId: string) => {
  const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK>(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }

  const { mainData } = agentData;
  const { reviewResponseData } = mainData;

  if (!reviewResponseData) {
    throw new Error("An error occurred");
  }

  const { approvedIds = [], editedIds = [] } = mainData;
  const finalGapJson: FinalGapReviewResponse[] = [];

  (reviewResponseData as any[]).forEach((item, id) => {
    const controlObject = item.reduce((acc : any, curr: any) => {
      acc[curr.key] = curr.value
      return acc
    }, {})

    const gap: FinalGapReviewResponse = {
      approved: (approvedIds as string[])?.includes(`${id as number}`),
      edited: (editedIds as string[])?.includes(`${id as number}`),
      id,
      ...controlObject
    };

    finalGapJson.push(gap);
  });

  return finalGapJson;
};


export const handleCustomGapAssessmentAutoSave = async ({ agentId, markAsComplete }: AutoSaveFunctionArgs) => {
  const gapData = generateFinalCustomGapAssessmentJSON(agentId)

  const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK>(agentId);

  if (!agentData) {
    throw new Error("An error occurred");
  }

  const { stepData } = agentData;
  const { staleUrl, selectedId } = agentData.mainData;

  const { setStaleUrl, updateAgentStepData } = getAgentStateActions();

  const editStepData = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

  if (!editStepData) {
    throw new Error("An occurred while saving");
  }

  const stepUrl = editStepData?.data?.url;

  let currentStaleUrl = (staleUrl || stepUrl) as string;
  let markAsCompleteDone = false;

  if (!currentStaleUrl) {
    const signedUrl = await getSignedUrl({
      file_names: ["updated_gaps.json"],
      max_age: 86400,
    });
    const updatedStep = {
      ...editStepData,
      data: {
        url: signedUrl[0],
      },
      status: markAsComplete ? AgentSessionStatus.COMPLETE : AgentSessionStatus.INPUT_NEEDED,
    };
    const updatedSteps = stepData.map((step) => {
      if (step.id === updatedStep.id) {
        return updatedStep;
      }
      return step;
    });
    updateAgentStepData(agentId, updatedSteps);
    await updateAgentSessionStep(updatedStep);
    currentStaleUrl = signedUrl[0];
    markAsCompleteDone = true;
    setStaleUrl(agentId, currentStaleUrl);
  }
  const expiryDate = new URL(currentStaleUrl).searchParams.get("se");
  if (expiryDate) {
    const expiry = new Date(expiryDate);
    const currentTime = new Date();
    const diff = expiry.getTime() - currentTime.getTime();
    if (diff < 0) {
      const signedUrl = await getSignedUrl({
        stale_urls: [currentStaleUrl],
        max_age: 86400,
      });
      currentStaleUrl = signedUrl[0];
      setStaleUrl(agentId, currentStaleUrl);
    }
  }

  const control = gapData.find((item) => item.id === selectedId);
  if (!control) return;

  const { emitControlEdit } = useAgentSocket();
  try {
    const agentData = getAgentData<
      AGENT_TYPES.GAP_ASSESSMENT,
      GapAssessmentTypes.SOC2
    >(agentId);
    const editedField = agentData?.mainData.lastEditedField;
    control.edited = true;
    await emitControlEdit(
      currentStaleUrl,
      control,
      control.id,
      agentId,
      editStepData.id,
      editedField ?? "",
    );
  } catch (error) {
    console.error("Error in emitControlEdit:", error);
    throw error;
  }
};

const getResponseDataTitle = (key: string) => {
  switch(key){
    case "sources":
      return "Other Reference(s)";
    case "testing_procedures":
    case "toe":
    case "tod":
      return "Testing Details";
  }

  return convertSnakeToCapitalized(key);
};

export const processFileForSOC2Type2Audit = async (url: string, sessionId: string) => {

  const sessionControls = await getAgentSessionControls(sessionId);
  const gapJson: FinalGapReviewResponse[] = sessionControls.data?.map((data: AgentSessionControl) => data.soc2_audit) || [];


  // Add cache busting parameter
  const urlWithNoCaching = `${url}${url.includes("?") ? "&" : "?"}_t=${Date.now()}`;

  // Sort gapJson in ascending order of trust_id

  const session = await getAgentSession(sessionId ?? "");

  if(session?.data?.session.type === AgentSessionType.GAP_CUSTOM_FRAMEWORK){
    processFileForCustomGapAssessmentReview(url, sessionId, sessionControls, session)
  }else 
{
  let versionScheme: ResponseSchemaVersion | undefined = undefined;
  if (session.data.session.type !== AgentSessionType.SOC2_GAP) {
    versionScheme = "v2";
  }

  const ids: string[] = [];
  const keysToOmit: string[] = [
    "id",
    "confidence",
    "approved",
    "edited",
    "llm_instructions",
    "trust_id",
    "control_number",
    "control",
    "category",
    "assessment_criteria",
    "retriever_questions",
    "control_id",
    "zania_control_id",
    "justification",
    "tsc_id",
    "tsc",
    "uid",
    "criteria",
    "testing_procedures",
    "version",
    session.data.session.type !== AgentSessionType.SOC2_GAP_TYPE2 ? "toe" : "tod",
    "edited_state",
    "evidence",
    "selected",
    "reassesed",
    "observations",
    "improvement_recommendations",
    "index",
    "source_urls",
    "file_names"
  ];
  const tableRows: Soc2GapReviewResponseTableRow[] = [];
  const approvedIds: string[] = [];
  const editedIds: string[] = [];
  const reviewResponse = new Map<
    string,
    ReviewResponseData<keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>>[]
  >(
    gapJson.map((gap) => {
      const id = gap.id;
      ids.push(id);
      const tableRow: Soc2GapReviewResponseTableRow = {
        id: id,
        confidence: gap.confidence ?? "",
        sources: gap.sources ?? [],
        tag: gap.trust_id ?? gap.control_id ?? "",
        control: gap.control ?? "",
        compliant: (gap.compliance_status ? gap.compliance_status.replace(/ /g, "-") : "") as ComplianceStatus,
        status: gap.approved ? "approved" : gap.edited ? "edited" : "none",
      };
      tableRows.push(tableRow);
      if (gap.approved) {
        approvedIds.push(id);
      }
      if (gap.edited) {
        editedIds.push(id);
      }

      const responseData = Object.entries({ ...gap }).reduce(
        (acc, [key, value]) => {
          const data: ReviewResponseData<
            keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>
          > = {
            type: getRenderType(key, versionScheme ?? (gap.version as ResponseSchemaVersion)),
            value: value,
            key: key as keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>,
            title: getResponseDataTitle(key), // use this instead of getFieldTitle because accoding to figma ui we dont need to other reference or reference
            shouldRender: !keysToOmit.includes(key),
          };
          acc.push(data);
          return acc;
        },
        [] as ReviewResponseData<
          keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>
        >[],
      );

      // order of things testing procedure -> observation -> gaps -> improvement oppurtunies -> test results -> compliance status -> sources

      const orderedKeys = [
        "compliance_status",
        session.data.session.type === AgentSessionType.SOC2_GAP_TYPE2 ? "toe" : "tod",
        "test_results",
        "gaps",
        "improvement_opportunities",
        // "recommendations",
        "sources",
      ];

      responseData.sort((a, b) => {
        const indexA = orderedKeys.indexOf(a.key as string);
        const indexB = orderedKeys.indexOf(b.key as string);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });

      return [id, responseData];
    }),
  );

  const currentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(sessionId);
  if (!currentData) {
    throw new Error("An error occurred");
  }

  const stepData = currentData.stepData.map((step) => {
    if (
      [
        AgentSessionStepType.SOC2_TYPE2_AUDIT,
        AgentSessionStepType.SOC2_TYPE1,
        AgentSessionStepType.SOC2_TYPE2,
      ].includes(step.type)
    ) {
      // Preserve UNDER_REASSESSMENT status if it was already set during reassessment
      if (step.status !== AgentSessionStatus.UNDER_REASSESSMENT) {
        step.status = AgentSessionStatus.COMPLETE;
      }
    }
    
    if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
      // Preserve UNDER_REASSESSMENT status if it was already set during reassessment
      if (step.status !== AgentSessionStatus.UNDER_REASSESSMENT) {
        step.status = AgentSessionStatus.INPUT_NEEDED;
      }
      step.data = {
        url: url,
      };
    }
    return step;
  });
  const agentSubType =
    session.data.session.type === AgentSessionType.SOC2_GAP_TYPE1
      ? GapAssessmentTypes.SOC2_TYPE1
      : session.data.session.type === AgentSessionType.SOC2_GAP_TYPE2
        ? GapAssessmentTypes.SOC2_TYPE2
        : GapAssessmentTypes.SOC2;

  const agentData: Partial<
    AgentData<
      AGENT_TYPES.GAP_ASSESSMENT,
      GapAssessmentTypes.SOC2 | GapAssessmentTypes.SOC2_TYPE1 | GapAssessmentTypes.SOC2_TYPE2
    >
  > = {
    agentType: AGENT_TYPES.GAP_ASSESSMENT,
    subType: agentSubType,
    sessionData: session.data.session as SOC2GapAgentSession,
    stepData,
    mainData: {
      ...currentData.mainData,
      reviewResponseData: reviewResponse,
      reviewResponseIds: ids,
      tableRows,
      searchFor: ["control"],
      searchTerm: "",
      approvedIds,
      editedIds,
    },
  };

  const { updateAgentData } = getAgentStateActions();
  updateAgentData<
    AGENT_TYPES.GAP_ASSESSMENT,
    GapAssessmentTypes.SOC2 | GapAssessmentTypes.SOC2_TYPE1 | GapAssessmentTypes.SOC2_TYPE2
  >(sessionId, agentData);
}
};

export const processFileForCustomGapAssessmentReview = async (url: string, sessionId: string, sessionControlsDetails: any, sessionDetails?: AgentSessionResponse) => {

  let sessionControls = sessionControlsDetails;
  if(!sessionControls){
    sessionControls = await getAgentSessionControls(sessionId);
  }
  const gapJson: any[] = sessionControls.data?.map((data: AgentSessionControl) => data.gap_custom_framework) || [];


  // Add cache busting parameter
  const urlWithNoCaching = `${url}${url.includes("?") ? "&" : "?"}_t=${Date.now()}`;

  // Sort gapJson in ascending order of trust_id

  let session = sessionDetails;
  if(!session){
    session = await getAgentSession(sessionId ?? "");
  }

  let versionScheme: ResponseSchemaVersion | undefined = undefined;
  if (session.data.session.type !== AgentSessionType.SOC2_GAP) {
    versionScheme = "v2";
  }

  const ids: string[] = [];
  const keysToOmit: string[] = [
    "id",
    "confidence",
    "approved",
    "edited",
    "llm_instructions",
    "trust_id",
    "control_number",
    "control",
    "category",
    "assessment_criteria",
    "retriever_questions",
    "control_id",
    "zania_control_id",
    "justification",
    "tsc_id",
    "tsc",
    "uid",
    "criteria",
    // "testing_procedures",
    "version",
    session.data.session.type !== AgentSessionType.SOC2_GAP_TYPE2 ? "toe" : "tod",
    "edited_state",
    "evidence",
    "selected",
    "reassesed",
    "improvement_recommendations",
    "index",
    "source_urls",
    "file_names",
    "backend_to_ui_keys_mappings",
    "framework_id",
    "control_description",
    "control_specifications"
  ];
  const tableRows: Soc2GapReviewResponseTableRow[] = [];
  const approvedIds: string[] = [];
  const editedIds: string[] = [];
  const reviewResponse = new Map<
    string,
    ReviewResponseData<keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>>[]
  >(
    gapJson.map((gap) => {
      const id = gap.id;
      ids.push(id);
      const tableRow: Soc2GapReviewResponseTableRow = {
        id: id,
        confidence: gap.confidence ?? "",
        sources: gap.sources ?? [],
        tag: gap.trust_id ?? gap.control_id ?? "",
        control: gap.control || gap.control_description || "",
        compliant: (gap.compliance_status ? gap.compliance_status.replace(/ /g, "-") : "") as ComplianceStatus,
        status: gap.approved ? "approved" : gap.edited ? "edited" : "none",
      };
      tableRows.push(tableRow);
      if (gap.approved) {
        approvedIds.push(id);
      }
      if (gap.edited) {
        editedIds.push(id);
      }

      // Handle Control Specifications (additional columns added by user to be displayed in 2 cols)
      const defaultCustomControlKeys = new Set(['control_id', 'control_description', "testing_procedures"])
      const controlSpecificationMapping = gap.backend_to_ui_keys_mappings.reduce((acc: any, curr: any) => {
        if (!defaultCustomControlKeys.has(curr.key)) {
          acc.set(curr.key, {
            title: curr.ui_key,
            value: gap[curr.key]
          });
          }
          return acc;
          }, new Map());

      const controlSpecificationObject = {
        type: "control_specifications",
        value: controlSpecificationMapping,
        key: "control_specifications",
        title: "Control Specifications",
        shouldRender: !!controlSpecificationMapping.size,
      }

      if(gap.gaps){
        gap.gaps = gap.gaps.map((x: any) => ({...x, sources: x.references}))
      }

      if(gap.test_results){
        gap.test_results = gap.test_results.map((x: any) => ({...x, sources: x.references}))
      }

      if(gap.observations){
        gap.observations = gap.observations.map((x: any) => ({...x, sources: x.references}))
      }

      

      const responseData = Object.entries({ ...gap }).reduce(
        (acc, [key, value]) => {
          const data: ReviewResponseData<
        keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>
          > = {
        type: getRenderType(key, versionScheme ?? (gap.version as ResponseSchemaVersion), value as any),
        value: value,
        key: key as keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>,
        title: getResponseDataTitle(key),
        shouldRender: !(keysToOmit.includes(key) || controlSpecificationMapping.has(key)),
          };
          acc.push(data);
          return acc;
        },
        controlSpecificationMapping.size
          ? [controlSpecificationObject] as ReviewResponseData<
          keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>
        >[]
          : [] as ReviewResponseData<
          keyof AgentSpecificReviewResponseType<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>
        >[],
      );

      // order of things testing procedure -> observation -> gaps -> improvement oppurtunies -> test results -> compliance status -> sources

      const orderedKeys = [
        "control_specifications",
        "compliance_status",
        "testing_procedures",
        session.data.session.type === AgentSessionType.SOC2_GAP_TYPE2 ? "toe" : "tod",
        "test_results",
        "gaps",
        "improvement_opportunities",
        // "recommendations",
        "sources",
      ];

      responseData.sort((a, b) => {
        const indexA = orderedKeys.indexOf(a.key as string);
        const indexB = orderedKeys.indexOf(b.key as string);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      });

      return [id, responseData];
    }),
  );

  const currentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(sessionId);
  if (!currentData) {
    throw new Error("An error occurred");
  }

  const stepData = currentData.stepData.map((step) => {
    if (
      [
        AgentSessionStepType.GAP_CUSTOM_FRAMEWORK,
      ].includes(step.type)
    ) {
      step.status = AgentSessionStatus.COMPLETE;
    }
    
    if (step.type === AgentSessionStepType.EDIT_RESPONSE) {
      step.status = AgentSessionStatus.INPUT_NEEDED;
      step.data = {
        url: url,
      };
    }
    return step;
  });
  const agentSubType = GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK;

  const agentData: Partial<
    AgentData<
      AGENT_TYPES.GAP_ASSESSMENT,
      GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK
    >
  > = {
    agentType: AGENT_TYPES.GAP_ASSESSMENT,
    subType: agentSubType,
    sessionData: session.data.session as CustomGapAgentSession,
    stepData,
    mainData: {
      ...currentData.mainData,
      reviewResponseData: reviewResponse,
      reviewResponseIds: ids,
      tableRows,
      searchFor: ["control"],
      searchTerm: "",
      approvedIds,
      editedIds,
    },
  } as any;

  const { updateAgentData } = getAgentStateActions();
  updateAgentData<
    AGENT_TYPES.GAP_ASSESSMENT,
    GapAssessmentTypes.SOC2 | GapAssessmentTypes.SOC2_TYPE1 | GapAssessmentTypes.SOC2_TYPE2
  >(sessionId, agentData as any);
};

export const getSoc2JsonFromApi = async (sessionId: string) => {
  try {
    const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(sessionId);
    if (!agentData) {
      throw new Error("An error occurred");
    }
    const { responseQuality, sessionData, stepData } = agentData;

    addNotification({
      type: "success",
      title: "Report Download in Progress...",
      message: "If the download does not begin within 15 seconds, please click the ‘Download’ button",
    });

    const data = await getRAGExcelFromJSON({
      task: agentData.sessionData.type,
      output_format: "excel",
      response_quality: responseQuality ?? AiResponseType.LITE,
      agent_session_id: sessionId
    });

    const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();

    setFinalFileUrl(sessionId, data.data.file_url);

    // Direct download using the file URL
    const additionalDetails = {} as any
    if((agentData.subType as any) === GapAssessmentTypes.GAP_CUSTOM_FRAMEWORK){
      const frameworkId = (agentData?.sessionData as any)?.gap_custom_framework?.framework_id ?? "";
      const frameworksList = await getFrameworksList() as any;
      additionalDetails.frameworkName = frameworksList?.mapping?.[frameworkId] ?? "Custom Framework";
    }
    downloadFileWithCheck(getFileNameForDownloadReport(sessionData, additionalDetails), data.data.file_url, (errorMsg: string) => {
      addNotification({
        type: "error",
        title: "Download failed",
        message: errorMsg,
      });
    });

    

    const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

    const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);
    const updatedStepData = stepData.map((step) => {
      if (step.id === editResponseStep?.id) {
        return { ...step, status: AgentSessionStatus.COMPLETE };
      }
      if (step.id === downloadStepData?.id) {
        return {
          ...step,
          data: {
            url: data.data.file_url,
          },
        };
      }
      return step;
    });

    if (downloadStepData?.id) {
      await updateAgentSessionStep({
        ...downloadStepData,
        data: {
          url: data.data.file_url,
        },
      });
    }

    updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
  } catch (error) {
    console.error(error);
    addNotification({
      type: "error",
      message: "Error in downloading the file",
      title: "Error",
    });
  }
};

export const handleEndSoc2Session = async (sessionId: string, navigate: NavigateFunction) => {
  try {
    const { setFinalFileUrl, updateAgentStepData } = getAgentStateActions();
    const agentData = getAgentData<AGENT_TYPES.GAP_ASSESSMENT, GapAssessmentTypes.SOC2>(sessionId);
    if (!agentData) {
      throw new Error("An error occurred");
    }

    const { stepData } = agentData;

    const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

    //if user directly clicks on end session without download report
    if (editResponseStep?.status !== AgentSessionStatus.COMPLETE) {
      // const gaps = JSON.stringify(generateFinalSoc2GapJSON(sessionId));
      // const blob = new Blob([gaps], { type: "application/json" });

      const signedUrl = await getSignedUrl({
        file_names: ["updated_gap.json"],
        max_age: 86400,
      });

      // await uploadFileReq(signedUrl[0], blob);
      const editResponseStep = stepData?.find((step) => step.type === AgentSessionStepType.EDIT_RESPONSE);

      // Process the JSON file first
      await processFileForSOC2Type2Audit(signedUrl[0], sessionId);

      const data = await getRAGExcelFromJSON({
        task: agentData.sessionData.type,
        output_format: "excel",
        response_quality: agentData.responseQuality ?? AiResponseType.LITE,
        agent_session_id: sessionId
      });

      setFinalFileUrl(sessionId, data.data.file_url);

      const downloadStepData = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

      const updatedStepData = stepData.map((step) => {
        if (step.id === editResponseStep?.id) {
          return {
            ...step,
            status: AgentSessionStatus.COMPLETE,
            data: {
              url: signedUrl[0], //  JSON URL for future processing
            },
          };
        }
        if (step.id === downloadStepData?.id) {
          return {
            ...step,
            data: {
              url: data.data.file_url,
            },
            status: AgentSessionStatus.COMPLETE,
          };
        }
        return step;
      });

      if (downloadStepData?.id) {
        await updateAgentSessionStep({
          ...downloadStepData,
          data: {
            url: data.data.file_url,
          },
          status: AgentSessionStatus.COMPLETE,
        });
      }

      updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    } else {
      const prepareReportStep = stepData?.find((step) => step.type === AgentSessionStepType.PREPARE_REPORT);

      const updatedStepData = stepData.map((step) => {
        if (step.id === prepareReportStep?.id) {
          return { ...step, status: AgentSessionStatus.COMPLETE };
        }
        return step;
      });

      if (prepareReportStep?.id) {
        await updateAgentSessionStep({
          ...prepareReportStep,
          status: AgentSessionStatus.COMPLETE,
        });
      }

      await new Promise<void>((resolve) => {
        updateAgentStepData(sessionId, updatedStepData as AgentSessionStep[]);
        setTimeout(resolve, 0);
      });

      const successPath = `/${ROUTES.AGENT}/${AGENT_ROUTES.SUCCESS}/${sessionId}`;
      navigate(successPath);
    }
  } catch (error) {
    console.error(error);
    addNotification({
      type: "error",
      message: "Error in ending the session",
      title: "Error",
    });
  }
};
